import { Component, EventEmitter, Input, Output, OnChanges, SimpleChanges, inject } from '@angular/core';
import { CanaryTreeNode } from './custom-resource-tree-node.model';
import { KubernetesDialogTypeEnum } from './kubernetes-status-dialog-payload.model';
import { CanaryRoutingOption } from '../../deployment-common/deployment-process.model';
import { InventoryService } from '../inventory.service';
import { catchError, distinctUntilChanged } from 'rxjs/operators';
import { HttpErrorResponse } from '@angular/common/http';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { ModalNotificationService } from '../../notification/modal-notification.service';
import _ from 'lodash';

@Component({
    selector: 'adm4-canary-deployment-generation-output',
    template: `
    <div class="full-height-flex">
      <div class="remaining-space-flex-content-wrapper content-container">
        <div>
          <div class='text-with-icon-container warning'>
            <mat-icon class='warning-icon'>report_problem</mat-icon>
            @if (isPromote) {
              <p>After promoting the Secondary deployments, your Primary deployments will be removed including all the related resources. The promoted Secondary deployment will become your Primary version and all the traffic goes to that version.</p>
            }
            @if (!isPromote) {
              <p>After rolling back the Secondary deployments, they will be removed, including all the related resources. The Primary deployment will receive all the traffic again.</p>
            }
          </div>
          @if (!displayCustomResourceTree) {
            <div class='img-container'>
              <img src='{{imgResource}}' alt=''/>
              <div class='img-description'>
                @if (canaryConfig.header) {
                  <div>Header name = {{canaryConfig?.header}}</div>
                }
                @if (canaryConfig.cookie) {
                  <div>Cookie name = {{canaryConfig?.cookie}}</div>
                }
                @if (canaryConfig.percentage) {
                  <div>User base (%) = {{canaryConfig.percentage}}% to Secondary</div>
                }
              </div>
            </div>
          }
        </div>
        <div class='resources-container'>
          <div class="resource-list-container">
            <div class="remaining-space-flex-content-wrapper">
              <div class="remaining-space-flex-content">
                <div class="full-height-flex step-content-card tree-view">
                  <div class='text-with-icon-container' (click)='toggleCustomResource()'>
                    <i class="{{displayCustomResourceTree ? 'fa fa-caret-down' : 'fa fa-caret-right'}}" aria-hidden="true"></i>
                    <div class='resource-toggle'>Resources being changed</div>
                  </div>
                  @if (displayCustomResourceTree) {
                    <div class='resource-tree-container' [ngClass]='boxShadowClass'>
                      <div class="remaining-space-flex-content">
                        <div class='tree-view'>
                          @if (k8sResourceTree?.length) {
                            <adm4-canary-tree-viewer
                              [nodes]='k8sResourceTree'
                              [kubernetesActionType]='kubernetesActionType'
                            (nodeSelected)='selectNode($event)'></adm4-canary-tree-viewer>
                          }
                        </div>
                      </div>
                    </div>
                  }
                  <div class='comment-wrapper'>
                    <div class="comment-title">
                      Comment (optional)
                    </div>
                    <textarea class='form-control admn4-textarea-input'
                      placeholder='Add your comments'
                      [(ngModel)]='commitMessage'
                    (ngModelChange)='onCommentMessageChange($event)'></textarea>
                    <div class="validation-message-container">
                      @if (commentHasError$ | async) {
                        <adm4-validation-message [isError]='true' [message]='"You have exceeded the maximum character limit of 150."'></adm4-validation-message>
                      }
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div class="file-details-container">
            @if (displayCustomResourceTree) {
              <adm4-k8s-custom-resource-details
                [node]='selectedNode'
                [inventoryKey]='inventoryKey'
                [kubernetesActionType]='kubernetesActionType'>
              </adm4-k8s-custom-resource-details>
            }
          </div>
        </div>
      </div>
      <div mat-dialog-actions>
        <button class='admn4-button-text' (click)='closeClicked.emit()' [disabled]='promoteInProgress'>Cancel</button>
        <button type='button' class='admn4-button-ellipse-blue' [disabled]='promoteInProgress || (commentHasError$ | async)' (click)='triggerDeployments()'>
          @if (promoteInProgress) {
            <mat-spinner [diameter]="20" class="button-spinner"></mat-spinner>
          }
          @if (!promoteInProgress) {
            <span>{{kubernetesActionType}} deployments</span>
          }
          @if (promoteInProgress) {
            <span>Please wait</span>
          }
        </button>
      </div>
    </div>
    `,
    styleUrls: ['./canary-deployment-generation-output.component.scss'],
    standalone: false
})
export class CanaryDeploymentGenerationOutputComponent implements OnChanges {
  private inventoryService = inject(InventoryService);
  private modalNotificationService = inject(ModalNotificationService);

  @Input() k8sResourceTree: CanaryTreeNode[] = [];
  @Input() inventoryKey: string;
  @Input() kubernetesActionType: KubernetesDialogTypeEnum;
  @Input() canaryConfig: CanaryRoutingOption;
  @Output() closeClicked: EventEmitter<void> = new EventEmitter();
  public selectedNode: CanaryTreeNode;
  boxShadowClass = 'default-inventory-box-shadow';
  displayCustomResourceTree = false;
  commitMessage = '';
  isPromote: boolean = false;

  _commentHasError$: BehaviorSubject<boolean> = new BehaviorSubject(false);
  commentHasError$: Observable<boolean> = this._commentHasError$.asObservable().pipe(distinctUntilChanged(_.isEqual));
  promoteInProgress = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes.kubernetesActionType) {
      this.isPromote = KubernetesDialogTypeEnum.PROMOTE === this.kubernetesActionType;
    }
  }

  selectNode(selectedNode: CanaryTreeNode): void {
    this.selectedNode = selectedNode;
  }

  toggleCustomResource() {
    this.displayCustomResourceTree = !this.displayCustomResourceTree;
  }

  get imgResource(): string {
    if (this.kubernetesActionType === KubernetesDialogTypeEnum.PROMOTE) {
      return 'assets/promote.png';
    }
    return 'assets/rollback.png';
  }

  triggerDeployments() {
    if (this.kubernetesActionType === KubernetesDialogTypeEnum.PROMOTE) {
      this.promoteInProgress = true;
      this.inventoryService.promoteCanaryDeployment(this.inventoryKey, this.getTruncatedCommitMessage()).pipe((promoteProcess) => {
          return promoteProcess;
        },
        catchError((err: HttpErrorResponse) => {
          this.promoteInProgress = false;
          console.error(`Something went wrong while promoting deployment`, err);
          this.modalNotificationService.openErrorDialog({title: 'Could not promote the deployments', description: err?.error?.error?.detail});
          return of(null);
        })).subscribe((promoteResult) => {
        if (!_.isNil(promoteResult)) {
          this.promoteInProgress = false;
          this.closeClicked.emit();
          this.modalNotificationService.openInfoDialog({title: 'Promote in progress', description: 'The promotion of the deployments are now running in the background. You can track their progress on the Kubernetes Status screen.'});
        }
      });
    } else {
      this.inventoryService.rollbackCanaryDeployment(this.inventoryKey, this.getTruncatedCommitMessage()).pipe((rollbackProcess) => {
          return rollbackProcess;
        },
        catchError((err: HttpErrorResponse) => {
          console.error(`Something went wrong while rollback deployment`, err);
          this.modalNotificationService.openErrorDialog({title: 'Could not rollback the deployments', description: err.error.error.detail});
          return of(null);
        })).subscribe((rollbackResult) => {
        if (!_.isNil(rollbackResult)) {
          this.closeClicked.emit();
          this.modalNotificationService.openSuccessDialog({title: 'Success', description: 'The deployments have been successfully rolled back. Please note that deleting the related resources may take some time. Therefore, existing environment might be running for a few minutes.'});
        }
      });
    }
  }

  private getTruncatedCommitMessage() {
    return _.isEmpty(this.commitMessage) ? undefined : this.commitMessage;
  }

  onCommentMessageChange(commentMessage: string): void {
    const isInvalidCommentLength = commentMessage.length > 150;
    this._commentHasError$.next(isInvalidCommentLength);
  }
}
